import { DestroyRef, Injectable, computed, effect, inject, signal } from '@angular/core';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { OrderService } from '../../services/order.service';
import { OFFLINE_DB, VERSION_DE_LA_BASE, db } from '../offline/db';
import { TrabajoEnCurso } from './trabajo-en-curso';

/**
 * F2.7 — el POS se actualiza solo (plan de mayoristas, docs 4425aab).
 *
 * <h3>Lo que pasaba</h3>
 * `provideServiceWorker` registraba el service worker y nada escuchaba
 * `SwUpdate`. Tras desplegar, la pestaña abierta seguía con el POS anterior hasta
 * que alguien forzaba la actualización: a un comercio le llegaba un arreglo días
 * después, sin saberlo, y cada verificación en staging medía código viejo. Peor:
 * el bundle viejo y el nuevo llegaban a ejecutarse sobre la misma base local.
 *
 * <h3>Cuándo se busca (sin temporizador, R14)</h3>
 * Al arrancar, al volver el evento `online` y al iniciar sesión. F2.7b: además al
 * volver la pestaña a primer plano y justo después de cobrar (el carrito queda
 * vacío), como mucho una vez cada 30 min con marca de tiempo guardada: una caja
 * abierta todo el día también se entera de un despliegue a mediodía, y una caja
 * con mucho volumen no pide el manifiesto cientos de veces. Sin red, o con la
 * pestaña oculta, no se busca nada. Solo se pide el `ngsw.json` estático.
 *
 * <h3>Cuándo se aplica</h3>
 * Solo en un momento seguro: con el carrito vacío. Una venta a medias no se
 * pierde por una recarga: se espera a que se cobre o se vacíe, con un aviso
 * discreto y un botón para hacerlo antes. El outbox vive en IndexedDB y no se
 * toca: sobrevive a la recarga.
 */
@Injectable({ providedIn: 'root' })
export class ActualizacionDeLaAppService {
  private readonly sw = inject(SwUpdate, { optional: true });
  private readonly orden = inject(OrderService);
  private readonly base = inject(OFFLINE_DB, { optional: true }) ?? db;
  private readonly destroyRef = inject(DestroyRef);

  /** Recarga la página. Se sustituye en las pruebas. */
  recargar: () => void = () => location.reload();
  /** Reloj (ms). Se sustituye en las pruebas. */
  reloj: () => number = () => Date.now();

  private readonly versionLista = signal(false);
  /** F2.7b — otra pestaña abrió la base local con un esquema mayor que el de este bundle. */
  private readonly baseMasNueva = signal(false);
  /** F2.7b — hubo una versión nueva de la base pero ya se recargó por eso hace poco: solo aviso. */
  private readonly avisoSinRecarga = signal(false);
  private readonly irrecuperable = signal(false);
  private aplicando = false;
  private iniciado = false;

  readonly hayVersionNueva = computed(() => this.versionLista() || this.baseMasNueva() || this.avisoSinRecarga());
  readonly avisoIrrecuperable = this.irrecuperable.asReadonly();
  private readonly carritoVacio = computed(() => this.orden.currentOrder().items.length === 0);
  private readonly trabajo = inject(TrabajoEnCurso);
  /** Momento seguro para recargar: sin venta en el carrito ni otro trabajo sin guardar (cobro de cartera). */
  private readonly momentoSeguro = computed(() => this.carritoVacio() && !this.trabajo.hayTrabajo());

  /** F2.7b — como mucho una búsqueda «por actividad» cada este tiempo. */
  static readonly INTERVALO_MINIMO_MS = 30 * 60 * 1000;
  static readonly CLAVE_ULTIMA_BUSQUEDA = 'suresell.pwa.ultimaBusqueda';
  /** F2.7b — freno de bucle: como mucho una recarga por la base local cada este tiempo. */
  static readonly VENTANA_FRENO_MS = 10 * 60 * 1000;
  static readonly CLAVE_RECARGA_POR_BASE = 'suresell.pwa.recargaPorBase';

  constructor() {
    // En cuanto haya versión lista y el carrito esté vacío, se aplica.
    effect(() => {
      if ((this.versionLista() || this.baseMasNueva()) && this.momentoSeguro()) {
        void this.aplicar();
      }
    });
    // F2.7b — tras cobrar: el carrito pasa de tener líneas a quedar vacío.
    let habiaLineas = false;
    effect(() => {
      const vacio = this.carritoVacio();
      if (vacio && habiaLineas && this.iniciado) {
        void this.buscarConLimite();
      }
      habiaLineas = !vacio;
    });
  }

  iniciar(): void {
    if (this.iniciado) return;
    this.iniciado = true;
    // Cada escucha se quita al destruirse el servicio: un servicio que ya no existe
    // no debe seguir decidiendo búsquedas ni recargas.
    const escuchar = <T extends EventTarget>(objetivo: T, tipo: string, fn: EventListener) => {
      objetivo.addEventListener(tipo, fn);
      this.destroyRef.onDestroy(() => objetivo.removeEventListener(tipo, fn));
    };

    // F2.7b — otra pestaña del POS sube la versión de la base local. Dexie ya cierra
    // la conexión y la reabre en la siguiente operación (dexie.mjs:5941-5947); eso
    // se conserva para que la venta en curso pueda guardarse. Aquí solo se decide
    // si esta pestaña debe pasar a la versión nueva.
    const alCambiarVersion = (ev: IDBVersionChangeEvent) => this.alCambiarLaVersionDeLaBase(ev.newVersion);
    this.base.on('versionchange', alCambiarVersion);
    this.destroyRef.onDestroy(() => this.base.on('versionchange').unsubscribe(alCambiarVersion));

    if (!this.sw?.isEnabled) return;
    const sw = this.sw;
    const suscripciones = [
      sw.versionUpdates.subscribe((evento: VersionEvent) => {
        if (evento.type === 'VERSION_READY') {
          this.versionLista.set(true);
        } else if (evento.type === 'VERSION_INSTALLATION_FAILED') {
          console.warn('[actualización] no se pudo instalar la versión nueva:', evento.error);
        }
      }),
      sw.unrecoverable.subscribe(() => {
        // El service worker ya no puede servir esta versión: seguir en ella
        // rompería la caja. Se avisa y se recarga aunque haya carrito.
        this.irrecuperable.set(true);
        this.recargar();
      }),
    ];
    this.destroyRef.onDestroy(() => suscripciones.forEach((x) => x.unsubscribe()));
    if (typeof window !== 'undefined') {
      escuchar(window, 'online', () => void this.buscar());
    }
    if (typeof document !== 'undefined') {
      // F2.7b — la cajera vuelve a la pestaña o desbloquea el equipo.
      escuchar(document, 'visibilitychange', () => {
        if (document.visibilityState === 'visible') void this.buscarConLimite();
      });
    }
    void this.buscar();
  }

  /**
   * Recarga solo si la otra pestaña trae un esquema MAYOR que el de este bundle: la
   * versión nativa es Dexie × 10, y un «schema patch» de un bundle más viejo suma 1
   * (p. ej. 51 sobre 50), lo que no es una versión nueva de la app; recargar por eso
   * metería a la pestaña buena en un bucle. Y como mucho una recarga por esta causa
   * cada 10 min (marca en sessionStorage); si ya recargó, queda el aviso.
   */
  private alCambiarLaVersionDeLaBase(nuevaNativa: number | null): void {
    if (!nuevaNativa || Math.floor(nuevaNativa / 10) <= VERSION_DE_LA_BASE) return;
    if (this.recargoPorLaBaseHacePoco()) {
      this.avisoSinRecarga.set(true);
      return;
    }
    this.baseMasNueva.set(true);
  }

  private recargoPorLaBaseHacePoco(): boolean {
    try {
      const v = Number(sessionStorage.getItem(ActualizacionDeLaAppService.CLAVE_RECARGA_POR_BASE));
      return Number.isFinite(v) && v > 0 && this.reloj() - v < ActualizacionDeLaAppService.VENTANA_FRENO_MS;
    } catch {
      return false;
    }
  }

  /** Lo llama la sesión al entrar: un login es un buen momento para mirar si hay versión nueva. */
  alIniciarSesion(): void {
    void this.buscar();
  }

  /** «Actualizar»: con el carrito vacío, ya. Con venta en curso no se toca; se aplica al vaciarse. */
  async actualizarAhora(): Promise<void> {
    if (this.momentoSeguro()) {
      await this.aplicar();
    }
  }

  /** F2.7b — búsqueda por actividad: no más de una cada 30 min, contando las demás. */
  private async buscarConLimite(): Promise<void> {
    const ultima = this.ultimaBusqueda();
    const ahora = this.reloj();
    // Una marca «del futuro» (el reloj del equipo retrocedió) no frena: si frenara,
    // la caja dejaría de buscar versiones hasta que el reloj la alcanzara.
    if (ultima !== null && ultima <= ahora && ahora - ultima < ActualizacionDeLaAppService.INTERVALO_MINIMO_MS) return;
    await this.buscar();
  }

  private ultimaBusqueda(): number | null {
    try {
      const v = Number(localStorage.getItem(ActualizacionDeLaAppService.CLAVE_ULTIMA_BUSQUEDA));
      return Number.isFinite(v) && v > 0 ? v : null;
    } catch {
      return null;
    }
  }

  private async buscar(): Promise<void> {
    if (!this.sw?.isEnabled) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    try {
      localStorage.setItem(ActualizacionDeLaAppService.CLAVE_ULTIMA_BUSQUEDA, String(this.reloj()));
    } catch {
      /* sin almacenamiento: el límite no se guarda, pero la búsqueda sigue */
    }
    try {
      await this.sw.checkForUpdate();
    } catch {
      /* sin red a mitad o servidor caído: se vuelve a mirar en el próximo evento */
    }
  }

  private async aplicar(): Promise<void> {
    if (this.aplicando) return;
    this.aplicando = true;
    if (this.baseMasNueva()) {
      try {
        sessionStorage.setItem(ActualizacionDeLaAppService.CLAVE_RECARGA_POR_BASE, String(this.reloj()));
      } catch {
        /* sin sessionStorage el freno no se guarda; la recarga sigue */
      }
    }
    if (this.sw?.isEnabled) {
      try {
        await this.sw.activateUpdate();
      } catch {
        /* si no se pudo activar, la recarga igualmente carga la versión que el SW tenga lista */
      }
    }
    this.recargar();
  }

}
